import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { SupportAiBillingZod } from "./ai-billing";
import { ContactFormSettings } from "./contact-form";
import { ConversationTag } from "./conversation-tag";
import {
  CsatSurveyMethod,
  CsatSurveySendTime,
  CsatSurveyVariant,
} from "./csat-survey";
import { SenderEmail } from "./sender-email";
import { TeamSmsConfiguration } from "./sms-types";
import { SupportAiSettings } from "./support-ai-settings";
import { BaseSupportBilling, SupportVoiceBilling } from "./support-billing";
import { SupportInsightSlot } from "./support-insight-slots";
import {
  CloseActionMethod,
  PagesData,
  SendType,
} from "./support-settings-misc";
import { TextMessagingBilling } from "./text-message-billing";
import { TeamVoiceConfiguration } from "./voice-settings";

/**
 * Minimal structural stand-in for the model `IFaqTheme`
 * (`redo/model/src/faq/faq-body-schema.ts`).
 *
 * DELIBERATE LOOSENING: `IFaqTheme` is tied to the model-heavy FAQ parser, so
 * referencing it here would pull a model dependency into this model-free leaf.
 * `SupportSettings.faq` only needs to carry the persisted theme object through;
 * consumers that operate on the full FAQ theme import the model type directly.
 */
export type SupportFaqTheme = Record<string, unknown>;

/**
 * Support-owned projection of the team's support settings.
 *
 * Keep in sync with the generated DB-truth type `SupportSettingsRaw`
 * (`redo/backend/db/schema/src/types/team/SupportSettings.schema`).
 */
export interface SupportSettings {
  enabled: boolean;
  billing?: {
    textMessaging?: TextMessagingBilling;
    ai?: SupportAiBillingZod;
    base?: BaseSupportBilling;
    voice?: SupportVoiceBilling;
    paymentProvider?: PaymentProviderType;
  };
  csat?: {
    enabled: boolean;
    surveySendTime?: CsatSurveySendTime;
    surveyMethods: CsatSurveyMethod[];
    allowRemarks?: boolean;
    headerImage?: string;
    surveyMessage?: string;
    /** Percent of eligible conversations surveyed; absent means all of them. */
    sampleRate?: number;
    /** Only survey tickets received at these inboxes; empty means every inbox. */
    allowedInboxEmails?: string[];
    /** Marketing email-builder template used for the survey email. */
    emailTemplateId?: string;
    /** A/B test arms; when enabled, each surveyed conversation gets one. */
    abTestEnabled?: boolean;
    abTestVariants?: CsatSurveyVariant[];
  };
  closeAction: CloseActionMethod;
  defaultSendAction?: SendType;
  /** Fan the support inbox out across connected teams (default false). */
  allowUnifiedInbox?: boolean;
  useInProgressStatus?: boolean;
  keepAssigneeOnTicketReopen?: boolean;
  /** Release a user's open tickets back to the queue when their shift ends. */
  unassignOpenTicketsAtShiftEnd?: boolean;
  closeTicketsOnGmailArchive?: boolean;
  autoCloseArchivedGmailMessages?: boolean;
  showCreatedDateColumn?: boolean;
  /** Controls the contact form merchants can embed on their website */
  contactForm?: ContactFormSettings;
  receiveEmail?: string;
  senderEmails?: SenderEmail[];
  emailSignature?: string;
  aiEmailSignature?: string;
  tags?: ConversationTag[];
  // TODO: remove
  bccEmails?: boolean;
  tagSpam?: boolean;
  ticketAssignment: "manual" | "balanced" | "round-robin";
  usersForAutoAssignment: string[];
  autoAssignOnResponse?: boolean;
  autoReassignOnResponse?: boolean;
  autoReassignFromAiOnResponse?: boolean;
  autoReassignFromHumanOnResponse?: boolean;
  maxTicketsPerUser: string;
  metaPagesData?: {
    userId?: string;
    longLivedToken?: string;
    pagesData?: PagesData[];
  };
  excludedEmails?: string[];
  autoMergeExclusions?: string[];
  autoSplitSenders?: string[];
  ai?: SupportAiSettings;
  voice?: TeamVoiceConfiguration;
  sms?: TeamSmsConfiguration;
  instagram?: {
    autoCloseMentions: boolean;
    autoCloseRepliedStories: boolean;
    commentsEnabled: boolean;
    autoCloseExternal?: boolean;
  };
  facebook?: { autoCloseExternal?: boolean; commentsEnabled: boolean };
  propagateDeletes?: boolean;
  faq?: SupportFaqTheme;
  autoMergeEnabled?: boolean;
  mergeSuggestionsEnabled?: boolean;
  defaultEmail?: string;
  undoSend?: boolean;
  mobileMessage?: { enabled?: boolean; maxOrderValueCents?: number };
  /** Whether Support insights are on for this merchant. Absent means off. */
  insightsEnabled?: boolean;
  /** Saved Support dashboard and Teams banner insight picks. Absent uses defaults. */
  insights?: SupportInsightSlot[];
}

/** Support insights stay off until a merchant's setting is explicitly on. */
export function supportInsightsEnabled(
  settings: { insightsEnabled?: boolean | null } | null | undefined,
): boolean {
  return settings?.insightsEnabled === true;
}
